import { env } from '@/lib/env'
import { DRAWER_LABELS, isDrawer, type Drawer } from '../../workers/drawers'

/**
 * A FILE STAFF CAN OPEN FROM AN ANSWER THE OWNER WROTE (G46) — the client.
 *
 * Talks to POST /api/admin/facts/:id/attachment (workers/api.ts) and holds
 * every word the Facts page and the Documents page say about it, so the
 * wording is tested here (tests/fact-files-client.test.ts), not in JSX.
 * React-free, like src/lib/voice-notes.ts.
 *
 * NEVER TRUST THE WIRE: an attachment with no id or no name is read as none.
 * An Open button that names nothing, or a "staff can open" line over a file
 * that is not there, would both be lies.
 */

/** The kept file an answer offers to open. */
export interface FactAttachment {
  id: string
  name: string
  /** The file's own drawer; null is unfiled (owner only). */
  drawer: Drawer | null
  /** True when some staff get the answer but not the file: the file's drawer
   *  is narrower than the answer's. Worked out by the engine. */
  reachesFewer: boolean
}

export function toFactAttachment(raw: unknown): FactAttachment | null {
  if (raw === null || raw === undefined || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (typeof r.id !== 'string' || !r.id || typeof r.name !== 'string' || !r.name) return null
  return {
    id: r.id,
    name: r.name,
    drawer: isDrawer(r.drawer) ? r.drawer : null,
    // Missing or malformed reads as TRUE — "some staff may not get the button"
    // — the warning that sends him to check, never the silence that hides it.
    reachesFewer: r.reachesFewer !== false,
  }
}

// ---- Wording ----

export const ATTACH_ACTION = 'Add a file staff can open'
export const CHANGE_ACTION = 'Change the file'
export const REMOVE_ACTION = 'Take the file off'
export const CHOOSE_PROMPT = 'Which kept file should staff be able to open from this answer?'
export const NO_FILES = 'You have no kept files yet. Upload one on the Documents page first.'

/** The line under an answer that offers a file. */
export function attachmentLine(a: FactAttachment): string {
  return `Staff can open “${a.name}” from this answer.`
}

/** Said when the file's drawer is narrower than the answer's. */
export function reachesFewerLine(a: FactAttachment): string {
  const where = a.drawer === null ? 'is not in a drawer' : `is in ${DRAWER_LABELS[a.drawer]}`
  return `The file ${where}, so some staff who get this answer will not get the button. Move the file on the Documents page if they should.`
}

/** One option in the file chooser: the name, and where the file is filed. */
export function fileOptionLabel(f: { name: string; drawer: Drawer | null }): string {
  return f.drawer === null ? `${f.name} (not in a drawer)` : `${f.name} (${DRAWER_LABELS[f.drawer]})`
}

/** The extra sentence in the delete warning for a file answers offer. */
export function attachedAnswersWarning(count: number): string | null {
  if (count <= 0) return null
  return count === 1
    ? 'The 1 answer that offers it will stay, without the file.'
    : `The ${count} answers that offer it will stay, without the file.`
}

// ---- The request ----

export class FactFileError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly code?: string,
  ) {
    super(message)
    this.name = 'FactFileError'
  }
}

const SIGNED_OUT = 'Your sign-in has expired. Please sign in again.'
const OFFLINE = 'No connection. Check your network and try again.'
const FAILED = 'Could not change the file on that answer. Try again.'

/** Offer a kept file from an answer (documentId), or take it off (null). */
export async function setLiveFactAttachment(
  token: string | null,
  factId: string,
  documentId: string | null,
): Promise<FactAttachment | null> {
  if (!token) throw new FactFileError(SIGNED_OUT, 401)
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}/api/admin/facts/${encodeURIComponent(factId)}/attachment`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentId }),
    })
  } catch {
    throw new FactFileError(OFFLINE)
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null
  if (res.status === 401) throw new FactFileError(SIGNED_OUT, 401)
  if (!res.ok) {
    const message = typeof body?.message === 'string' && body.message ? body.message : FAILED
    throw new FactFileError(message, res.status, typeof body?.error === 'string' ? body.error : undefined)
  }
  return toFactAttachment(body?.attachment)
}
